import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const BFF_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';

export const axiosInstance = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const url = (error.config as any)?.url || '';
      // Don't redirect on background calls like config/permissions that may fail during auth transition
      if (!url.includes('/config') && !url.includes('/permissions')) {
        if (typeof window !== 'undefined') {
          window.location.href = AUTH_APP_URL;
        }
      }
    }
    return Promise.reject(error);
  },
);

export interface ApiError {
  message: string;
  status: number;
}

export const formatApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    let message = error.message;
    if (typeof data === 'string' && data.trim()) {
      message = data;
    } else if (data && typeof data === 'object') {
      const body = data as { message?: string; error?: string };
      if (body.message) message = body.message;
      else if (body.error) message = body.error;
    }
    return {
      message,
      status: error.response?.status || 500,
    };
  }
  return { message: 'An unexpected error occurred', status: 500 };
};
